// Van Life — co-op 2-player trick-taking game across Alaska's National Parks.
// See RULES.md for the rulebook. This is the module entry point:
//   types.ts — state/action/card types        data.ts  — cards, parks, constants
//   rng.ts   — seeded RNG                     rules.ts — dealing, tricks, evaluation
//   bots.ts  — heuristic + perfect bots

import {
  Card, Rank, Suit, ZoneCardId, ChallengeCardId,
  VanLifeMatchState, VanLifePlayer, VanLifePark,
  VanLifeConfig, VanLifeState, VanLifeAction, CompletedTrick,
} from './types';
import {
  TOTAL_WILDS, INITIAL_WILDS, WILDS_PER_MATCH, DEAL_SIZE, HAND_SIZE,
  ZONE_CARDS, CHALLENGE_CARDS, PARK_NAMES, PARK_IDS, PARK_ADJACENCY, PLAYER_COLORS,
} from './data';
import { shuffle } from './rng';
import { makeWild, drawDeckToSuit, resolveTrick, dealMatch, evaluateResult } from './rules';

export * from './types';
export {
  TOTAL_WILDS, INITIAL_WILDS, WILDS_PER_MATCH, DEAL_SIZE, HAND_SIZE,
  ZONE_CARDS, CHALLENGE_CARDS, PARK_NAMES, PARK_IDS,
  PARK_ADJACENCY, CHALLENGE_DIFFICULTY, zoneValidFinals,
} from './data';
export { getValidActions } from './rules';
export { getBotAction, getPerfectBotAction } from './bots';

// ── createGame ────────────────────────────────────────────────────────────────

export function createGame(
  config: VanLifeConfig,
  players: { id: string; name: string }[],
  seed = Date.now(),
): VanLifeState {
  let s = seed | 0;

  // Shuffle zone + challenge cards and assign one of each to each park
  let zoneIds = ZONE_CARDS.map(z => z.id) as ZoneCardId[];
  let challengeIds = CHALLENGE_CARDS.map(c => c.id) as ChallengeCardId[];
  [zoneIds, s] = shuffle(zoneIds, s);
  [challengeIds, s] = shuffle(challengeIds, s);

  const parks: VanLifePark[] = PARK_IDS.map((id, i) => ({
    id,
    name: PARK_NAMES[id],
    zoneCardId: zoneIds[i],
    challengeCardId: challengeIds[i],
    conquered: false,
  }));

  // Assign random starting positions (can be same park)
  let parkIdsCopy = [...PARK_IDS];
  [parkIdsCopy, s] = shuffle(parkIdsCopy, s);

  // Randomize roles: shuffle the two players so either may be Track (P1) or
  // Challenge (P2). Color and starting park follow the role index.
  let orderedPlayers = players.slice(0, 2);
  [orderedPlayers, s] = shuffle(orderedPlayers, s);

  const gamePlayers: VanLifePlayer[] = orderedPlayers.map((p, i) => ({
    id: p.id,
    name: p.name,
    playerNumber: (i + 1) as 1 | 2,
    color: PLAYER_COLORS[i],
    currentParkId: parkIdsCopy[i],
  }));

  // Shuffle the 9-card wild suit (unique ranks); pool takes the first
  // INITIAL_WILDS, the rest wait in the wild deck (one joins per match)
  let wildRanks = Array.from({ length: TOTAL_WILDS }, (_, i) => (i + 1) as Rank);
  [wildRanks, s] = shuffle(wildRanks, s);
  const wildPool = wildRanks.slice(0, INITIAL_WILDS).map(makeWild);
  const wildDeck = wildRanks.slice(INITIAL_WILDS).map(makeWild);

  const [match, s2] = dealMatch(1, gamePlayers, wildPool, s);

  return {
    config,
    phase: 'trick-taking',
    matchNumber: 1,
    players: gamePlayers,
    parks,
    wildPool,
    wildDeck,
    pendingMoves: {},
    match,
    gameWon: false,
    rng: s2,
  };
}

// ── applyAction ───────────────────────────────────────────────────────────────

export function applyAction(state: VanLifeState, action: VanLifeAction): VanLifeState {
  switch (action.type) {
    case 'chooseMove':   return applyChooseMove(state, action);
    case 'discardCards': return applyDiscardCards(state, action);
    case 'playCard':     return applyPlayCard(state, action);
    case 'advanceMatch': return applyAdvanceMatch(state, action);
    default: return state;
  }
}

function applyChooseMove(
  state: VanLifeState,
  action: Extract<VanLifeAction, { type: 'chooseMove' }>,
): VanLifeState {
  if (state.phase !== 'movement') return state;
  const { playerId, targetParkId } = action;
  const player = state.players.find(p => p.id === playerId);
  if (!player) return state;
  if (state.pendingMoves[playerId] !== undefined) return state;

  const adj = PARK_ADJACENCY[player.currentParkId] ?? [];
  const canMove = targetParkId === player.currentParkId || adj.includes(targetParkId);
  if (!canMove) return state;

  const pending = { ...state.pendingMoves, [playerId]: targetParkId };
  const bothChosen = state.players.every(p => pending[p.id] !== undefined);

  if (!bothChosen) return { ...state, pendingMoves: pending };

  // Both committed — move players and start next match
  const newPlayers = state.players.map(p => ({ ...p, currentParkId: pending[p.id] }));

  const [match, newRng] = dealMatch(
    state.matchNumber,
    newPlayers,
    state.wildPool,
    state.rng,
  );

  return {
    ...state,
    players: newPlayers,
    pendingMoves: {},
    phase: 'trick-taking',
    match,
    rng: newRng,
  };
}

function applyDiscardCards(
  state: VanLifeState,
  action: Extract<VanLifeAction, { type: 'discardCards' }>,
): VanLifeState {
  if (state.phase !== 'trick-taking' || !state.match) return state;
  const { playerId, cardIds } = action;
  const m = state.match;
  if (m.subPhase !== 'discard') return state;
  if (m.pendingDiscards[playerId]) return state;

  const numToDiscard = DEAL_SIZE - HAND_SIZE;
  if (cardIds.length !== numToDiscard) return state;

  const hand = m.hands[playerId] ?? [];
  const cardSet = new Set(cardIds);
  if (cardIds.some(id => !hand.find(c => c.id === id))) return state;

  const newHand = hand.filter(c => !cardSet.has(c.id));
  const newHands = { ...m.hands, [playerId]: newHand };
  const newPendingDiscards = { ...m.pendingDiscards, [playerId]: true };
  const allDiscarded = state.players.every(p => newPendingDiscards[p.id]);

  return {
    ...state,
    match: {
      ...m,
      hands: newHands,
      pendingDiscards: newPendingDiscards,
      subPhase: allDiscarded ? 'lead' : 'discard',
    },
  };
}

function applyPlayCard(
  state: VanLifeState,
  action: Extract<VanLifeAction, { type: 'playCard' }>,
): VanLifeState {
  if (state.phase !== 'trick-taking' || !state.match) return state;
  const { playerId, cardId } = action;
  const m = state.match;
  if (m.subPhase === 'discard') return state;

  const followId = state.players.find(p => p.id !== m.leadPlayerId)!.id;
  const isLead   = m.subPhase === 'lead'   && m.leadPlayerId === playerId;
  const isFollow = m.subPhase === 'follow' && followId === playerId;
  if (!isLead && !isFollow) return state;

  const hand = m.hands[playerId] ?? [];
  const cardIdx = hand.findIndex(c => c.id === cardId);
  if (cardIdx === -1) return state;
  const played = hand[cardIdx];

  const newHand = hand.filter((_, i) => i !== cardIdx);
  const newHands = { ...m.hands, [playerId]: newHand };

  if (isLead) {
    // Lead player must follow deck suit when deck leads; free choice when player leads
    if (m.deckLeads && m.currentDeckCard) {
      const deckSuit = m.currentDeckCard.suit;
      if (deckSuit !== 'Wild' && played.suit !== 'Wild' && played.suit !== deckSuit) {
        if (hand.some(c => c.suit === deckSuit)) return state;
      }
    }

    let updatedMatch: VanLifeMatchState = { ...m, hands: newHands, leadCard: played, subPhase: 'follow' };

    // Deck draws to matching suit after player leads (skipped when deck exhausted)
    if (!m.deckLeads && state.config.deckDrawsToSuit && m.currentDeckCard && played.suit !== 'Wild') {
      const [drawnCard, remainingDeck] = drawDeckToSuit(m.currentDeckCard, m.parkDeck, played.suit as Suit);
      updatedMatch = { ...updatedMatch, currentDeckCard: drawnCard, parkDeck: remainingDeck };
    }

    return { ...state, match: updatedMatch };
  }

  // Follow player: must follow deck suit (deck leads) or lead player's suit (player leads)
  if (m.deckLeads && m.currentDeckCard) {
    const deckSuit = m.currentDeckCard.suit;
    if (deckSuit !== 'Wild' && played.suit !== 'Wild' && played.suit !== deckSuit) {
      if (hand.some(c => c.suit === deckSuit)) return state;
    }
  } else if (!m.deckLeads) {
    const leadSuit = m.leadCard!.suit;
    if (leadSuit !== 'Wild' && played.suit !== 'Wild' && played.suit !== leadSuit) {
      if (hand.some(c => c.suit === leadSuit)) return state;
    }
  }

  // Follow card played — resolve trick
  const leadCard   = m.leadCard!;
  const followCard = played;
  const deckCard   = m.currentDeckCard;          // null = deck exhausted; players-only trick
  const nextDeckCard   = m.parkDeck[0] ?? null;  // null = deck now exhausted
  const newParkDeck    = m.parkDeck.slice(1);

  const winnerId = resolveTrick(leadCard, followCard, deckCard, m.leadPlayerId, followId, m.deckLeads);

  const completedTrick: CompletedTrick = {
    trickNumber: m.trickNumber,
    leadPlayerId: m.leadPlayerId,
    cards: { [m.leadPlayerId]: leadCard, [followId]: followCard, ...(deckCard ? { deck: deckCard } : {}) },
    winnerId,
    winnerCard:
      winnerId === 'deck'         ? deckCard! :
      winnerId === m.leadPlayerId ? leadCard : followCard,
  };

  const completedTricks = [...m.completedTricks, completedTrick];

  // Update tracker: P1 win → -1, P2 win → +1, deck → no change
  const p1Id = state.players.find(p => p.playerNumber === 1)!.id;
  let tracker = m.tracker;
  if (winnerId === p1Id)        tracker = Math.max(1, tracker - 1);
  else if (winnerId !== 'deck') tracker = Math.min(7, tracker + 1);
  const trackerHistory = [...m.trackerHistory, tracker];
  const deckTrickCount = m.deckTrickCount + (winnerId === 'deck' ? 1 : 0);

  const nextLeadId = winnerId === 'deck' ? m.leadPlayerId : winnerId as string;
  const isLast = m.trickNumber >= m.totalTricks;

  const baseMatch: VanLifeMatchState = {
    ...m,
    hands: newHands,
    parkDeck: newParkDeck,
    currentDeckCard: nextDeckCard,
    // Deck can only lead if it still has a card to flip; otherwise prior leader leads freely.
    deckLeads: winnerId === 'deck' && nextDeckCard !== null,
    leadCard: null,
    followCard: null,
    subPhase: 'lead',
    completedTricks,
    tracker,
    trackerHistory,
    deckTrickCount,
    leadPlayerId: nextLeadId,
    trickNumber: m.trickNumber + 1,
  };

  if (!isLast) return { ...state, match: baseMatch };

  // ── Last trick: evaluate results ──
  const result = evaluateResult(
    state.players, state.parks,
    completedTricks, tracker, trackerHistory, deckTrickCount,
  );

  const newParks = state.parks.map(park => {
    if (park.conquered) return park;
    return result.parkResults.some(r => r.parkId === park.id && r.conquered)
      ? { ...park, conquered: true }
      : park;
  });

  // Move WILDS_PER_MATCH wilds from the wild deck into the pool for next deal
  const movedWilds  = state.wildDeck.slice(0, WILDS_PER_MATCH);
  const newWildDeck = state.wildDeck.slice(WILDS_PER_MATCH);
  const newWildPool = [...state.wildPool, ...movedWilds];

  const allConquered = newParks.every(p => p.conquered);
  const gameOver = allConquered || state.matchNumber >= 5;

  return {
    ...state,
    phase: gameOver ? 'finished' : 'match-result',
    gameWon: allConquered,
    parks: newParks,
    players: state.players,
    wildPool: newWildPool,
    wildDeck: newWildDeck,
    rng: state.rng,
    match: { ...baseMatch, result },
  };
}

function applyAdvanceMatch(
  state: VanLifeState,
  action: Extract<VanLifeAction, { type: 'advanceMatch' }>,
): VanLifeState {
  if (state.phase !== 'match-result') return state;
  // "Continue" is a host control with no hidden decision — any player in the
  // game may advance (the UI gates the button on the leader, who may be P1 or P2).
  if (!state.players.some(p => p.id === action.playerId)) return state;

  return {
    ...state,
    phase: 'movement',
    matchNumber: state.matchNumber + 1,
    pendingMoves: {},
    match: null,
  };
}

// ── getPlayerView ─────────────────────────────────────────────────────────────

export function getPlayerView(state: VanLifeState, playerId: string | null): VanLifeState {
  // During trick-taking (including discard): hide partner's hand
  if (playerId === null || state.phase !== 'trick-taking' || !state.match) return state;
  const filteredHands: Record<string, Card[]> = {};
  for (const [pid, hand] of Object.entries(state.match.hands)) {
    filteredHands[pid] = pid === playerId ? hand : new Array(hand.length).fill({ suit: 'Unknown', rank: 0, id: '?' });
  }
  return { ...state, match: { ...state.match, hands: filteredHands } };
}

// ── describe ──────────────────────────────────────────────────────────────────

export function describe(_config: VanLifeConfig): string {
  return [
    'Van Life — Co-op 2-player trick-taking across Alaska\'s 8 National Parks.',
    '',
    'GOAL: Conquer all 8 parks within 5 matches. P1 conquers their park via its Zone Card',
    '(where the shared 7-space tracker lands); P2 conquers theirs via its Challenge Card.',
    '',
    'DECK: 27 cards (Green/Blue/Tan, ranks 1–9) + 4 wild cards shuffled in at game start.',
    '  Wilds are a 9-card suit of unique ranks 1–9 (no duplicate wilds, so no ties).',
    '  After each match, 1 more wild joins the deck (match 1 = 31 cards, match 5 = 35 cards).',
    '  Each player is dealt 10 cards and discards 2 before the match begins.',
    '  The remainder forms the Park Deck (11–15 cards).',
    '',
    'DISCARD PHASE: Before tricks begin, each player selects 2 cards to discard.',
    '  Knowing your zone/challenge target, shape your hand for the match ahead.',
    '  Discards are simultaneous and hidden from your partner.',
    '',
    'TRICKS (8 per match):',
    '  1. Trick 1 (and after any deck win): Park Deck leads, setting the led suit.',
    '     After a player wins: that player leads next, freely choosing their suit.',
    '     When a player leads, the Park Deck draws cards until it finds the led suit',
    '     (or a wild); skipped cards are discarded, thinning the deck.',
    '  2. Lead player plays a card, then follow player plays a card.',
    '     Follow player must follow the led suit if able; wilds are always playable.',
    '  3. Winner: highest card of led suit. Wilds beat all non-wilds (higher rank wins).',
    '  4. Tracker: P1 win → move left; P2 win → move right; Deck win → no movement.',
    '  Park Deck exhaustion: once the deck runs out, the Deck can no longer win — only',
    '  players compete. Small early decks run out sooner (easier early matches); larger',
    '  late decks rarely do (harder late matches).',
    '',
    'ROLES: P1 is the Track Player; P2 is the Challenge Player. Roles are fixed all game.',
    '  Track Player (P1) conquers their park by meeting its Zone condition (tracker position).',
    '  Challenge Player (P2) conquers their park by meeting its Challenge condition.',
    '  Same park: zone met (P1) OR challenge met (P2) → conquered.',
    '',
    'WILDS beat all non-wild suits. Higher-rank wild beats lower-rank wild.',
    '  Wilds are dealt into player hands and the park deck like any other card.',
    '',
    'MOVEMENT: After each match, each player moves to any adjacent park (or stays).',
    '',
    'Win: All 8 parks conquered. Lose: Match 5 ends with any park unconquered.',
  ].join('\n');
}
